/**
 * Etichette e helper di presentazione della rete contatti.
 *
 * ⚠️ Qui NON stanno i nomi delle faccette né dei tag: quelli sono DATI in
 * Firestore (`platform_rete_faccette`, `platform_rete_tag`) e arrivano dal
 * database. In questo file solo ciò che è davvero fisso: i canali di
 * interazione e la formattazione.
 */

import { Timestamp } from 'firebase/firestore';
import { tokens } from '../../theme';
import type { CanaleInterazione } from '../../types/reteContatti';

/** Canali di interazione, nell'ordine in cui compaiono nella tendina. */
export const CANALE_ORDER: CanaleInterazione[] = [
  'videochiamata',
  'telefonata',
  'incontro',
  'whatsapp',
  'email',
  'altro',
];

export const CANALE_LABELS: Record<CanaleInterazione, string> = {
  videochiamata: 'videochiamata',
  telefonata: 'telefonata',
  incontro: 'incontro dal vivo',
  whatsapp: 'WhatsApp',
  email: 'email',
  altro: 'altro',
};

/**
 * Colore di ripiego per una faccetta senza colore configurato: lo slate delle
 * "tessere" del tema, non un grigio arbitrario.
 */
export const COLORE_FACCETTA_DEFAULT = tokens.color.tierFree.main;

/** Iniziali per il monogramma di una persona (max 2 lettere, sempre maiuscole). */
export function iniziali(nome: string): string {
  const parole = nome.trim().split(/\s+/).filter(Boolean);
  if (parole.length === 0) return '—';
  if (parole.length === 1) return parole[0].slice(0, 2).toUpperCase();
  return (parole[0][0] + parole[parole.length - 1][0]).toUpperCase();
}

/** Data breve italiana da un Timestamp Firestore (— se assente). */
export function formatDate(value: unknown): string {
  if (!(value instanceof Timestamp)) return '—';
  return value.toDate().toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Data ISO `YYYY-MM-DD` → `gg/mm/aaaa` (— se vuota o malformata). */
export function formatIsoDate(iso?: string): string {
  if (!iso) return '—';
  const parti = iso.split('-');
  if (parti.length !== 3) return iso;
  return `${parti[2]}/${parti[1]}/${parti[0]}`;
}

/** Oggi in formato ISO `YYYY-MM-DD` (default dei campi data). */
export function oggiIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const gg = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${gg}`;
}

/** Durata in secondi → `m:ss` per il contatore di registrazione. */
export function formatSecondi(secondi: number): string {
  const m = Math.floor(secondi / 60);
  const s = secondi % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
